import cx from 'classnames'
import { SanityGrid, SanityColor } from '@data/sanity/queries/types/modules'
import { getGridSizeClass, getItemOrderClass } from '@lib/dom'
import Icon from '@components/icon'
import { colorClasses } from '@components/portable-text/blocks/colored-text'
import GridBlock from './grid-block'
import { backgroundColorClassMap } from '@lib/color'
import { borderRadiusClassMap } from '@lib/border'

type GridProps = SanityGrid

interface GridColumnFrameProps {
  frameColor: SanityColor
}

const getSpacingClasses = (
  // TODO: Add type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  spacing: any,
  type: 'padding' | 'margin',
  side?: 'top' | 'right' | 'bottom' | 'left',
) => {
  const t = type.charAt(0)
  const s = spacing?.[type]

  const top = {
    [`${t}t-${s?.top}`]: s?.top,
    [`sm:${t}t-${s?.smTop}`]: s?.smTop,
    [`md:${t}t-${s?.mdTop}`]: s?.mdTop,
    [`lg:${t}t-${s?.lgTop}`]: s?.lgTop,
    [`xl:${t}t-${s?.xlTop}`]: s?.xlTop,
  }

  const right = {
    [`${t}r-${s?.right}`]: s?.right,
    [`sm:${t}r-${s?.smRight}`]: s?.smRight,
    [`md:${t}r-${s?.mdRight}`]: s?.mdRight,
    [`lg:${t}r-${s?.lgRight}`]: s?.lgRight,
    [`xl:${t}r-${s?.xlRight}`]: s?.xlRight,
  }

  const bottom = {
    [`${t}b-${s?.bottom}`]: s?.bottom,
    [`sm:${t}b-${s?.smBottom}`]: s?.smBottom,
    [`md:${t}b-${s?.mdBottom}`]: s?.mdBottom,
    [`lg:${t}b-${s?.lgBottom}`]: s?.lgBottom,
    [`xl:${t}b-${s?.xlBottom}`]: s?.xlBottom,
  }

  const left = {
    [`${t}l-${s?.left}`]: s?.left,
    [`sm:${t}l-${s?.smLeft}`]: s?.smLeft,
    [`md:${t}l-${s?.mdLeft}`]: s?.mdLeft,
    [`lg:${t}l-${s?.lgLeft}`]: s?.lgLeft,
    [`xl:${t}l-${s?.xlLeft}`]: s?.xlLeft,
  }

  if (side === 'top') {
    return top
  }

  if (side === 'right') {
    return right
  }

  if (side === 'bottom') {
    return bottom
  }

  if (side === 'left') {
    return left
  }

  return {
    ...top,
    ...right,
    ...bottom,
    ...left,
  }
}

const GridColumnFrame = ({ frameColor }: GridColumnFrameProps) => {
  return (
    <>
      <Icon
        name="FrameCorner"
        className={cx(
          'absolute -top-2 -left-2 xs:-top-4 xs:-left-4 md:-top-8 md:-left-8 text-[16px] sm:text-[24px] md:text-[32px] pointer-events-none',
          colorClasses[frameColor],
        )}
      />
      <Icon
        name="FrameCorner"
        className={cx(
          'absolute -top-2 -right-2 xs:-top-4 xs:-right-4 md:-top-8 md:-right-8 rotate-90 text-[16px] sm:text-[24px] md:text-[32px] pointer-events-none',
          colorClasses[frameColor],
        )}
      />
      <Icon
        name="FrameCorner"
        className={cx(
          'absolute -bottom-2 -right-2 xs:-bottom-4 xs:-right-4 md:-bottom-8 md:-right-8 rotate-180 text-[16px] sm:text-[24px] md:text-[32px] pointer-events-none',
          colorClasses[frameColor],
        )}
      />
      <Icon
        name="FrameCorner"
        className={cx(
          'absolute -bottom-2 -left-2 xs:-bottom-4 xs:-left-4 md:-bottom-8 md:-left-8 -rotate-90 text-[16px] sm:text-[24px] md:text-[32px] pointer-events-none',
          colorClasses[frameColor],
        )}
      />
    </>
  )
}

const Grid = ({
  size,
  spacing,
  columns: rawColumns,
  reverseSequence,
  background,
  fullWidth,
  noColumnGaps,
  noRowGaps,
}: GridProps) => {
  const columns = rawColumns.map((column, columnIndex) => {
    const columnClasses = column.sizes.map(
      ({
        breakpoint,
        width,
        justify,
        align,
        start,
        justifyContent,
        alignContent,
      }) =>
        getGridSizeClass(
          breakpoint,
          width,
          justify,
          align,
          start,
          justifyContent,
          alignContent,
        ),
    )
    const orderClasses = column.sizes.map((columnSize) => {
      return getItemOrderClass(
        columnIndex,
        columnSize.breakpoint,
        columnSize.width,
        size,
        reverseSequence,
      )
    })

    return {
      ...column,
      classes: [...columnClasses, ...orderClasses],
    }
  })

  return (
    <section
      className={cx(
        `${background ? backgroundColorClassMap[background] : ''}`,
        getSpacingClasses(spacing, 'margin'),
      )}
    >
      <div
        className={cx(
          'relative',
          `pt-${spacing?.padding?.top ?? 12} pb-${
            spacing?.padding?.bottom ?? 12
          }`,
          `sm:pt-${spacing?.padding?.smTop ?? 16} sm:pb-${
            spacing?.padding?.smBottom ?? 16
          }`,
          `md:pt-${spacing?.padding?.mdTop ?? 24} md:pb-${
            spacing?.padding?.mdBottom ?? 24
          }`,
          `lg:pt-${spacing?.padding?.lgTop ?? 32} lg:pb-${
            spacing?.padding?.lgBottom ?? 32
          }`,
          getSpacingClasses(spacing, 'padding', 'right'),
          getSpacingClasses(spacing, 'padding', 'left'),
          {
            container: !fullWidth,
          },
        )}
      >
        <div className="mx-auto">
          <div
            className={cx(
              `grid grid-cols-${size} gap-x-4 gap-y-8 sm:gap-x-8 lg:gap-x-10 lg:gap-y-10`,
              {
                '!gap-x-0': noColumnGaps,
                '!gap-y-0': noRowGaps,
              },
            )}
          >
            {columns.map((column) => (
              <div
                key={column._key}
                className={cx(
                  'relative',
                  column.hideOverflow ? 'overflow-hidden' : '',
                  column.classes,
                  `${column.background ? backgroundColorClassMap[column.background] : ''}`,
                  column.borderRadius
                    ? borderRadiusClassMap[column.borderRadius]
                    : '',
                  getSpacingClasses(column.spacing, 'margin'),
                  getSpacingClasses(column.spacing, 'padding'),
                )}
              >
                {!!column.frameColor && (
                  <GridColumnFrame frameColor={column.frameColor} />
                )}

                {column.blocks.map((block) => (
                  <GridBlock key={block._key} block={block} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Grid
